// src/components/AgentCreatorForm.tsx
import { useEffect, useState } from "react";
import { db } from "../firebase/firebase";
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  increment,
  getDocs,
  collection,
} from "firebase/firestore";
import CustomAlert from "./CustomAlert";
import FileUploader from "./FileUploader";

export default function AgentCreatorForm({
  userId,
  existingAgent,
}: {
  userId: string;
  existingAgent?: any;
}) {
  const [name, setName] = useState(existingAgent?.name || "");
  const [prompt, setPrompt] = useState(existingAgent?.prompt || "");
  const [model, setModel] = useState(existingAgent?.model || "gpt-4");
  const [availableTools, setAvailableTools] = useState<any[]>([]);
  // const [requestedTools, setRequestedTools] = useState<string[]>(
  //   existingAgent?.tools || []
  // );
  const [requestedTools, setRequestedTools] = useState<string[]>([]);

  const [tagsInput, setTagsInput] = useState(
    existingAgent?.tags?.join(", ") || ""
  );
  // Initialize isPublic from existingAgent or false
  const [isPublic, setIsPublic] = useState<boolean>(
    existingAgent?.isPublic ?? false
  );

  const [isPro, setIsPro] = useState(false);

  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId((prev) => prev + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };
  // Fetch subscriptionStatus to know if user is Pro
  useEffect(() => {
    const fetchUsage = async () => {
      const snap = await getDoc(doc(db, `users/${userId}`));
      const data = snap.data();
      setIsPro(data?.subscriptionStatus === "pro");
    };
    fetchUsage();
  }, [userId, existingAgent]);

  useEffect(() => {
    const fetchTools = async () => {
      const snap = await getDocs(collection(db, "tools"));
      setAvailableTools(
        snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    };
    fetchTools();
  }, []);

  useEffect(() => {
    if (existingAgent?.tools?.length) {
      setRequestedTools(existingAgent.tools);
    }
  }, [existingAgent]);

  const tags = tagsInput
    .split(",")
    .map((t: string) => t.trim().toLowerCase())
    .filter(Boolean);

  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();

    const agentId =
      existingAgent?.id || name.toLowerCase().replace(/\s+/g, "-");
    const agentRef = doc(db, `users/${userId}/agents/${agentId}`);

    // If user is NOT Pro, force isPublic=false
    const finalIsPublic = isPro ? isPublic : false;

    await setDoc(
      agentRef,
      {
        ...existingAgent,
        name,
        prompt,
        model,
        tags,
        tools: requestedTools,
        temperature: existingAgent?.temperature ?? 0.5,
        memoryEnabled: existingAgent?.memoryEnabled ?? true,
        isPublic: finalIsPublic, // override if not Pro
        favoriteCount: existingAgent?.favoriteCount ?? 0,
        creatorId: existingAgent?.creatorId ?? userId,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    if (!existingAgent) {
      const userRef = doc(db, `users/${userId}`);
      await setDoc(
        userRef,
        {
          agentsCreated: increment(1),
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );
    }

    showAlert(existingAgent ? "Agent updated!" : "Agent created!");
    // Reset only if you want to clear form for “new agent”
    setName("");
    setPrompt("");
    setModel("gpt-4");
    setTagsInput("");
    setIsPublic(false);
  };

  return (
    <form
      onSubmit={handleCreateAgent}
      className="bg-secondary border border-darkgray rounded-xl shadow-sm p-6 space-y-6 max-w-xl mx-auto text-sm max-h-[90vh] overflow-y-auto"
    >
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          message={alert.message}
          type={alert.type}
          onClose={() => closeAlert(alert.id)}
        />
      ))}
      <h2 className="text-lg font-semibold text-offwhite">
        {existingAgent ? "✏️ Edit Agent" : "➕ Create New Agent"}
      </h2>

      {/* Agent Name */}
      <div className="space-y-2">
        <label className="block font-medium text-offwhite">Agent Name</label>
        <input
          type="text"
          placeholder="e.g. Support Bot"
          className="w-full px-4 py-2 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>

      {/* Prompt */}
      <div className="space-y-2">
        <label className="block font-medium text-offwhite">Prompt</label>
        <textarea
          placeholder="Describe the agent's behavior..."
          className="w-full px-4 py-2 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          required
        />
      </div>

      {/* Model */}
      <div className="space-y-2">
        <label className="block font-medium text-offwhite">Model</label>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="w-full px-4 py-2 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
        >
          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
          <option value="gpt-4">GPT-4</option>
        </select>
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <label className="block font-medium text-offwhite">Tags</label>
        <input
          type="text"
          placeholder="e.g. support, sales"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          className="w-full px-4 py-2 border border-darkgray rounded-lg bg-charcoal text-offwhite focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
        />
      </div>

      {/* Tool Access */}
      <details className="bg-charcoal border border-darkgray rounded-lg p-4">
        <summary className="cursor-pointer text-offwhite font-medium mb-2">
          Tool Access
        </summary>
        {isPro && (
          <div className=" space-y-2">
            {availableTools.map((tool) => (
              <label
                key={tool.id}
                className="flex items-center gap-2 text-sm text-gray-300"
              >
                <input
                  type="checkbox"
                  checked={requestedTools.includes(tool.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setRequestedTools((prev) => [...prev, tool.id]);
                    } else {
                      setRequestedTools((prev) =>
                        prev.filter((t) => t !== tool.id)
                      );
                    }
                  }}
                />
                {tool.id} — {tool.description}
              </label>
            ))}
          </div>
        )}
      </details>

      {/* Is Public Checkbox (disabled for free users) */}
      <div className="flex items-center space-x-2">
        <input
          id="isPublic"
          type="checkbox"
          checked={isPublic}
          disabled={!isPro} // Free users cannot toggle
          onChange={(e) => setIsPublic(e.target.checked)}
          className="h-4 w-4 text-teal bg-charcoal border border-darkgray rounded focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
        />
        <label htmlFor="isPublic" className="text-offwhite text-sm">
          Make this agent <span className="font-medium">public</span>
        </label>
      </div>
      {!isPro && (
        <p className="text-xs text-gray-400">
          (Only Pro users can publish an agent. Upgrade to enable this.)
        </p>
      )}
      {isPro && existingAgent && (
        <FileUploader
          userId={userId}
          agentId={existingAgent?.id || name.toLowerCase().replace(/\s+/g, "-")}
        />
      )}

      {!isPro && (
        <p className="text-xs text-gray-400">
          File uploads & advanced tools are Pro only. Upgrade to unlock.
        </p>
      )}

      <button
        type="submit"
        className="w-full py-2 bg-teal text-offwhite rounded-lg font-medium hover:bg-teal/80 transition-all"
      >
        {existingAgent ? "Update Agent" : "Save Agent"}
      </button>
    </form>
  );
}
